import { useEffect, useRef, useState, useCallback } from 'react';

const useWebSocket = (userId) => {
  const [isConnected, setIsConnected] = useState(false);
  const [lastMessage, setLastMessage] = useState(null);
  const ws = useRef(null);
  const reconnectTimeout = useRef(null);
  const pingInterval = useRef(null);
  const reconnectAttempts = useRef(0);
  const isConnecting = useRef(false);
  const isMounted = useRef(true);
  const maxReconnectAttempts = 5;

  const cleanup = useCallback(() => {
    if (pingInterval.current) {
      clearInterval(pingInterval.current);
      pingInterval.current = null;
    }
    if (reconnectTimeout.current) {
      clearTimeout(reconnectTimeout.current);
      reconnectTimeout.current = null;
    }
    if (ws.current) {
      try {
        ws.current.onclose = null; // Prevent reconnect on intentional close
        ws.current.onerror = null;
        ws.current.onmessage = null;
        ws.current.onopen = null;
        ws.current.close(1000, 'Cleanup');
      } catch (e) {
        // Ignore errors during cleanup
      }
      ws.current = null;
    }
    isConnecting.current = false;
  }, []);

  const connect = useCallback(() => {
    if (!userId || !isMounted.current) {
      return;
    }

    // Prevent multiple simultaneous connection attempts
    if (isConnecting.current) {
      return;
    }

    // Don't reconnect if already connected
    if (ws.current && ws.current.readyState === WebSocket.OPEN) {
      return;
    }

    isConnecting.current = true;

    // Clean up existing connection first
    cleanup();

    // Get WebSocket URL from backend URL
    const backendUrl = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8001';
    const wsUrl = backendUrl.replace('https://', 'wss://').replace('http://', 'ws://');
    const wsEndpoint = `${wsUrl}/api/ws/${userId}`;
    
    try {
      ws.current = new WebSocket(wsEndpoint);

      ws.current.onopen = () => {
        if (!isMounted.current) return;
        console.log('✅ WebSocket connected');
        setIsConnected(true);
        reconnectAttempts.current = 0;
        isConnecting.current = false;
        
        // Start ping interval to keep connection alive (every 25 seconds)
        pingInterval.current = setInterval(() => {
          if (ws.current && ws.current.readyState === WebSocket.OPEN) {
            try {
              ws.current.send(JSON.stringify({ type: 'ping' }));
            } catch (e) {
              // Ignore ping errors
            }
          }
        }, 25000);
      };

      ws.current.onmessage = (event) => {
        if (!isMounted.current) return;
        try {
          const data = JSON.parse(event.data);
          // Ignore pong responses
          if (data.type === 'pong') return;
          setLastMessage(data);
        } catch (error) {
          // Ignore parse errors
        }
      };

      ws.current.onerror = () => {
        if (!isMounted.current) return;
        setIsConnected(false);
        isConnecting.current = false;
      };

      ws.current.onclose = (event) => {
        if (!isMounted.current) return;
        setIsConnected(false);
        isConnecting.current = false;
        
        if (pingInterval.current) {
          clearInterval(pingInterval.current);
          pingInterval.current = null;
        }
        
        // Always try to reconnect silently (connection drops are normal)
        // Don't reconnect if it was a clean intentional closure (code 1000)
        if (event.code !== 1000 && isMounted.current) {
          // Reset attempts for normal disconnects (1005, 1006 are typical idle timeouts)
          if (event.code === 1005 || event.code === 1006) {
            reconnectAttempts.current = 0;
          }
          
          if (reconnectAttempts.current < maxReconnectAttempts) {
            reconnectAttempts.current += 1;
            const delay = Math.min(2000 * Math.pow(2, reconnectAttempts.current - 1), 30000);
            
            reconnectTimeout.current = setTimeout(() => {
              if (isMounted.current) {
                connect();
              }
            }, delay);
          }
        }
      };
    } catch (error) {
      setIsConnected(false);
      isConnecting.current = false;
    }
  }, [userId, cleanup]);

  useEffect(() => {
    isMounted.current = true;
    
    // Delay initial connection slightly to avoid race conditions
    const initialDelay = setTimeout(() => {
      if (userId && isMounted.current) {
        connect();
      }
    }, 500);

    return () => {
      isMounted.current = false;
      clearTimeout(initialDelay);
      cleanup();
    };
  }, [userId, connect, cleanup]);

  const sendMessage = useCallback((message) => {
    if (ws.current && ws.current.readyState === WebSocket.OPEN) {
      ws.current.send(JSON.stringify(message));
    } else {
      console.warn('WebSocket is not connected');
    }
  }, []);

  const sendTypingIndicator = useCallback((toUserId, isTyping) => {
    sendMessage({
      type: 'typing',
      toUserId,
      isTyping
    });
  }, [sendMessage]);

  const notifyNewMessage = useCallback((receiverId, message) => {
    sendMessage({
      type: 'message',
      receiverId,
      message
    });
  }, [sendMessage]);

  const notifyMessageDeleted = useCallback((receiverId, messageId) => {
    sendMessage({
      type: 'message_deleted',
      receiverId,
      messageId
    });
  }, [sendMessage]);

  return {
    isConnected,
    lastMessage,
    sendTypingIndicator,
    notifyNewMessage,
    notifyMessageDeleted
  };
};

export default useWebSocket;
